import React, { useState } from "react";
import { ShieldCheck, Users, Ticket, Settings, Trophy } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { useAuth } from "../../context/AuthContext";
import { DiscordIcon } from "../../components/site/icons";
import AdminUsers from "./AdminUsers";
import AdminRaffles from "./AdminRaffles";
import AdminSettings from "./AdminSettings";
import AdminLeaderboard from "./AdminLeaderboard";

const Shell = ({ children }) => (
  <main className="relative z-10 min-h-[100svh] pt-36 sm:pt-40 pb-24">
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8">{children}</div>
  </main>
);

const AdminPage = () => {
  const { user, loading, login } = useAuth();
  const [tab, setTab] = useState("users");

  if (loading) {
    return (
      <Shell>
        <div className="glass-card py-20 flex justify-center">
          <p className="text-[#bcd9f2]">Checking access...</p>
        </div>
      </Shell>
    );
  }

  if (!user) {
    return (
      <Shell>
        <div className="glass-card py-16 px-6 flex flex-col items-center text-center">
          <ShieldCheck className="w-12 h-12 text-[#7fd0f5]" />
          <h1 className="heading-3d-sm text-[1.8rem] mt-4">Admins only</h1>
          <p className="mt-2 text-[#bcd9f2] text-[0.95rem]">Login with Discord to access the admin panel.</p>
          <button type="button" onClick={login} className="btn-discord mt-7">
            <DiscordIcon className="w-4 h-4" />
            Login with Discord
          </button>
        </div>
      </Shell>
    );
  }

  if (!user.is_admin) {
    return (
      <Shell>
        <div className="glass-card py-16 px-6 flex flex-col items-center text-center">
          <ShieldCheck className="w-12 h-12 text-[#ff8ba0]" />
          <h1 className="heading-3d-sm text-[1.8rem] mt-4">Access denied</h1>
          <p className="mt-2 text-[#bcd9f2] text-[0.95rem]">Your Discord account is not on the admin whitelist.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="section-badge">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.4} />
            Admin
          </span>
          <h1 className="heading-3d text-[2.2rem] sm:text-[3rem] mt-4">Control room</h1>
        </div>
        <p className="text-[#9fc5e8] text-[0.85rem]">
          Logged in as <span className="text-white font-bold">@{user.username}</span>
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="bg-white/5 border border-white/15 rounded-full p-1 h-auto flex-wrap">
          {[
            { v: "users", icon: Users, label: "Users" },
            { v: "raffles", icon: Ticket, label: "Raffles" },
            { v: "leaderboard", icon: Trophy, label: "Leaderboard" },
            { v: "settings", icon: Settings, label: "Settings" },
          ].map(({ v, icon: Icon, label }) => (
            <TabsTrigger
              key={v}
              value={v}
              className="rounded-full px-5 py-2.5 gap-2 text-[#9fc5e8] font-bold text-[0.78rem] tracking-[0.12em] uppercase data-[state=active]:bg-white/15 data-[state=active]:text-white data-[state=active]:shadow-none"
            >
              <Icon className="w-4 h-4" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="users" className="mt-8">
          <AdminUsers />
        </TabsContent>
        <TabsContent value="raffles" className="mt-8">
          <AdminRaffles />
        </TabsContent>
        <TabsContent value="leaderboard" className="mt-8">
          <AdminLeaderboard />
        </TabsContent>
        <TabsContent value="settings" className="mt-8">
          <AdminSettings />
        </TabsContent>
      </Tabs>
    </Shell>
  );
};

export default AdminPage;
